import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../context/NotificationContext"; 
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  BellIcon,
  CheckIcon
} from "@heroicons/react/24/solid";
import PageLayout, { PageHeader, Toolbar } from "../components/common/PageLayout";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, fetchNotifications } = useNotifications();
  
  useEffect(() => {
    if (typeof fetchNotifications === 'function') {
      fetchNotifications(true);
    }
  }, [fetchNotifications]);
  
  const [filter, setFilter] = useState("all");

  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success": return <CheckCircleIcon className="w-6 h-6 text-emerald-500" />;
      case "warning": return <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />;
      case "error": return <XCircleIcon className="w-6 h-6 text-red-500" />;
      default: return <InformationCircleIcon className="w-6 h-6 text-blue-500" />;
    }
  };

  // 🟢 SMART NAVIGATION LOGIC: Intercepts and repairs broken URLs
  const handleSafeNavigate = (link_url) => {
    if (!link_url) return;

    let finalUrl = link_url;

    // 1. Ensure it always starts with a forward slash
    if (!finalUrl.startsWith('/')) {
      finalUrl = '/' + finalUrl;
    }

    // 2. Auto-fix plural to singular mismatch from older database records
    if (finalUrl.startsWith('/projects/') && !finalUrl.startsWith('/admin/')) {
        finalUrl = finalUrl.replace('/projects/', '/admin/project/');
    }
    
    // 3. Add /admin prefix if missing for core modules
    const adminModules = ['project', 'task', 'inventory', 'workforce', 'EmployeePage', 'assets'];
    const needsAdmin = adminModules.some(m => finalUrl.includes(`/${m}`));
    
    if (needsAdmin && !finalUrl.startsWith('/admin')) {
        finalUrl = '/admin' + finalUrl;
    }

    console.log("Safe Navigate Redirecting To:", finalUrl);
    navigate(finalUrl);
  };

  const filteredNotifications = notifications.filter(note => {
    if (filter === "unread") return !note.is_read;
    return true; 
  });

  return (
    <PageLayout>
      {/* ── HEADER ── */}
      <PageHeader
        icon={BellIcon}
        iconColor="text-[#E8612C]"
        title="My Notifications"
        subtitle="View and manage your system alerts and messages."
        onRefresh={() => typeof fetchNotifications === 'function' && fetchNotifications(true)}
        actions={
          unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="px-4 py-2 bg-white dark:bg-[#161b22] border border-gray-300 dark:border-[#30363d] text-gray-700 dark:text-slate-200 rounded-xl text-xs font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition shadow-2xs flex items-center gap-2"
            >
              <CheckIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              Mark all as read
            </button>
          )
        }
      />

      {/* ── FILTER TABS / TOOLBAR ── */}
      <Toolbar
        tabs={['All Messages', 'Unread']}
        activeTab={filter === 'all' ? 'All Messages' : 'Unread'}
        onTabChange={(t) => setFilter(t === 'All Messages' ? 'all' : 'unread')}
      />

      {/* ── NOTIFICATIONS LIST ── */}
      <div className="space-y-4">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((note) => (
            <div 
              key={note.id}
              className={`bg-white dark:bg-[#161b22] rounded-2xl shadow-2xs border transition-all duration-200 overflow-hidden relative group ${
                !note.is_read 
                  ? "border-l-4 border-l-[#E8612C] border-y-gray-200 dark:border-y-[#30363d] border-r-gray-200 dark:border-r-[#30363d]" 
                  : "border-gray-200 dark:border-[#30363d] hover:border-gray-300 dark:hover:border-slate-600"
              }`}
            >
              <div className="p-4 sm:p-5 flex gap-4">
                
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(note.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1 sm:gap-4 mb-1">
                    <h3 className={`text-sm truncate ${!note.is_read ? "font-bold text-gray-900 dark:text-white" : "font-semibold text-gray-700 dark:text-slate-300"}`}>
                      {note.title}
                    </h3>
                    <span className="text-xs text-gray-400 dark:text-slate-500 whitespace-nowrap font-medium">
                      {formatDate(note.created_at)}
                    </span>
                  </div>
                  
                  <p className={`text-xs sm:text-sm leading-relaxed whitespace-pre-wrap ${!note.is_read ? "text-gray-800 dark:text-slate-200" : "text-gray-600 dark:text-slate-400"}`}>
                    {note.message}
                  </p>
                  
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 dark:border-[#30363d] pt-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-1 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 rounded-lg text-[10px] font-bold uppercase tracking-wider">
                        {note.sender_id ? "Admin Alert" : "System Event"}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {!note.is_read && (
                        <button 
                          onClick={() => markRead(note.id)}
                          className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 rounded-lg transition"
                        >
                          Mark as read
                        </button>
                      )}

                      {/* 🟢 Action Button using handleSafeNavigate */}
                      {note.link_url && (
                        <button 
                          onClick={() => {
                            if (!note.is_read) markRead(note.id);
                            handleSafeNavigate(note.link_url);
                          }}
                          className="px-3 py-1.5 text-xs font-bold text-white bg-[#E8612C] hover:bg-[#d45320] rounded-lg transition shadow-2xs flex items-center gap-1.5"
                        >
                          View Details
                          <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {!note.is_read && (
                  <div className="absolute top-6 right-5 hidden sm:block">
                    <div className="w-2.5 h-2.5 bg-[#E8612C] rounded-full shadow-2xs"></div>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] border-dashed rounded-2xl">
            <div className="bg-gray-50 dark:bg-slate-800 p-5 rounded-full mb-4">
              <BellIcon className="w-10 h-10 text-gray-300 dark:text-slate-600" />
            </div>
            <h3 className="text-base font-bold text-gray-800 dark:text-slate-200 mb-1">You're all caught up!</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 text-center max-w-sm">
              {filter === "unread" 
                ? "You have no unread notifications right now." 
                : "Your inbox is completely empty. We'll alert you when something happens."}
            </p>
            {filter === "unread" && (
              <button 
                onClick={() => setFilter("all")}
                className="mt-4 text-xs font-semibold text-[#E8612C] hover:underline"
              >
                View all past notifications
              </button>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default NotificationsPage;